import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, BookMarked, BedDouble,
  BarChart3, FileText, Menu, MoreHorizontal, X, Home, Users, Star, Tag,
  LogOut, Bell, IndianRupee, ClipboardCheck, ThumbsUp, ThumbsDown,
  XCircle, PlusCircle, ScrollText, KeyRound, UserX, Settings, Receipt, Sparkles, ShoppingCart,
  Building2, ChevronDown, UserCog,
} from "lucide-react";
import { useAuth }          from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import BookingToastContainer from "./BookingToast";
import Avatar, { ROLE_LABEL } from "./Avatar";
import { useSettings } from "../../context/SettingsContext";

const NAV = [
  { to: "dashboard",  label: "Dashboard",  Icon: LayoutDashboard },
  { to: "calendar",   label: "Calendar",   Icon: Calendar },
  { to: "bookings",   label: "Bookings",   Icon: BookMarked },
  { to: "customers",  label: "Customers",  Icon: Users, feature: "guestCrm" },
  { to: "rooms",      label: "Rooms",      Icon: BedDouble },
  { to: "rates",      label: "Rates",      Icon: IndianRupee },
  { to: "housekeeping", label: "Housekeeping", Icon: Sparkles, feature: "houseKeeping" },
  { to: "pos",        label: "Point of Sale", Icon: ShoppingCart, feature: "pos" },
  { to: "reviews",    label: "Reviews",    Icon: Star },
  { to: "promotions", label: "Promotions", Icon: Tag },
  { to: "financials", label: "Financials", Icon: BarChart3, feature: "reports" },
  { to: "reports",    label: "Reports",    Icon: FileText, feature: "reports" },
  { to: "expenses",   label: "Expenses",   Icon: Receipt, feature: "expenses" },
  { to: "logs",       label: "Logs",       Icon: ScrollText },
  // Owner-only server-side (settings:write/read are owner-only in
  // auth/permissions.js's GRANTS table) — hiding it for other roles too is
  // cosmetic, just avoids a nav link that always 403s.
  // Owner-only for the same reason: staff:read/write are owner-only, so
  // anyone else would see a page that can only refuse them.
  { to: "team",       label: "Team",       Icon: UserCog, ownerOnly: true },
  { to: "settings",   label: "Settings",   Icon: Settings, ownerOnly: true },
];

// Bottom tab bar (mobile only) shows the 4 most-used sections + a "More"
// sheet for everything else — a native-app tab pattern instead of a
// hamburger drawer, which reads as "website nav" rather than "app nav".
// Both derived from the role-filtered, feature-filtered nav computed
// inside the component (NAV itself stays the static, unfiltered source
// list). A nav entry with no `feature` key is always shown (core, not an
// optional module) — one with a `feature` key only shows once
// tenant_features has that flag on, so a disabled feature stops dead-
// ending in a 404 and just isn't in the nav at all. Server-side
// requireFeature() on every route stays the real enforcement either way.
function navFor(role, features) {
  return NAV.filter(item => (!item.ownerOnly || role === "owner") && (!item.feature || features?.[item.feature]));
}
const TABS_COUNT = 4;

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const NOTIFICATION_ICONS = {
  booking_new:           { Icon: PlusCircle,     color: "text-green-600 bg-green-50" },
  booking_approved:      { Icon: ThumbsUp,       color: "text-green-600 bg-green-50" },
  booking_rejected:      { Icon: ThumbsDown,     color: "text-red-600 bg-red-50" },
  booking_cancelled:     { Icon: XCircle,        color: "text-red-600 bg-red-50" },
  booking_no_show:       { Icon: UserX,          color: "text-orange-600 bg-orange-50" },
  booking_completed:     { Icon: LogOut,         color: "text-purple-600 bg-purple-50" },
  payment_recorded:      { Icon: IndianRupee,    color: "text-amber-600 bg-amber-50" },
  guest_form_submitted:  { Icon: ClipboardCheck, color: "text-blue-600 bg-blue-50" },
  review_submitted:      { Icon: Star,           color: "text-yellow-600 bg-yellow-50" },
  checkout_invoice_failed: { Icon: XCircle,      color: "text-red-600 bg-red-50" },
};

function ChangePasswordModal({ onClose, authHeaders }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [done, setDone]     = useState(false);

  async function submit() {
    setError("");
    if (newPassword.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (newPassword !== confirmPassword) { setError("New passwords don't match."); return; }

    setSaving(true);
    try {
      const res  = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <KeyRound size={16} className="text-gray-500" /> Change Password
          </h3>
          <button onClick={onClose} aria-label="Close"
            className="-mr-2 w-11 h-11 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {done ? (
          <div className="px-5 py-6 text-center space-y-3">
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
              Password updated successfully.
            </p>
            <button onClick={onClose}
              className="w-full py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium hover:bg-gray-900">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              {error && (
                <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
              )}
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                className="flex-1 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium
                  hover:bg-gray-900 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Update Password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function timeAgo(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/* Every tenant's PMS says who built it. Deliberately quiet — a property's
   own brand is what its staff and guests should see first — but always
   present, on the sidebar and in the mobile sheet alike, so it can't be
   missed on a phone where the sidebar never opens. The mark is a 64px copy
   of the logo (public/hostsync-mark.png); the wordmark baked into the full
   512px asset is illegible at this size, hence real text beside it. */
function PoweredByHostSync({ compact = false }) {
  return (
    <a href="https://hostsync.in" target="_blank" rel="noopener noreferrer"
      title="Powered by HostSync"
      className={`flex items-center justify-center gap-1.5 min-h-[44px] px-2
        text-[10px] font-medium text-gray-400 hover:text-gray-600 transition-colors
        ${compact ? "" : "w-full"}`}>
      <img src="/hostsync-mark.png" alt="" width="16" height="16"
        className="w-4 h-4 rounded-full flex-shrink-0" />
      {!compact && <span>Powered by <span className="font-semibold text-gray-500">HostSync</span></span>}
    </a>
  );
}

export default function AdminLayout() {
  const { authHeaders, logout, role, features, me } = useAuth();
  const nav       = navFor(role, features);
  const tabs      = nav.slice(0, TABS_COUNT);
  const moreItems = nav.slice(TABS_COUNT);
  const { feed, unreadCount, markAllRead }  = useNotifications();
  const { hotelName, logoUrl }        = useSettings();
  const navigate                      = useNavigate();
  const location                      = useLocation();

  const [sidebarOpen,   setSidebarOpen]   = useState(true);   // desktop collapse/expand
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);  // mobile "More" bottom sheet
  const [showLogout,   setShowLogout]   = useState(false);
  const [showBellMenu, setShowBellMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Multi-property switcher. Renders only for someone whose account is linked
  // to another property (backend/routes/tenantProperties.js) — for everyone
  // else this fetch returns an empty list and nothing appears, so a
  // single-property tenant's header is exactly as it was.
  const [properties,   setProperties]   = useState([]);
  const [showPropMenu, setShowPropMenu] = useState(false);
  const [switching,    setSwitching]    = useState(false);

  /* The admin's type scale — smaller and quieter, matching the platform
     console (index.css `html.tenant-admin`). Fonts only: colours and the
     tenant's chosen colour theme are untouched. On <html>, and removed on the
     way out, so the public booking pages keep their own sizes. */
  useEffect(() => {
    document.documentElement.classList.add("tenant-admin");
    return () => document.documentElement.classList.remove("tenant-admin");
  }, []);

  useEffect(() => {
    fetch(`${API}/properties`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => { if (j?.success) setProperties(j.data?.properties || []); })
      .catch(() => {}); // a switcher that fails to load is not worth an error to the user
  }, []);

  async function switchProperty(slug) {
    setSwitching(true);
    try {
      const res  = await fetch(`${API}/properties/switch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ toSlug: slug }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Couldn't switch property");
      // A real navigation to the other host — session cookies are host-scoped,
      // so there is no in-page way to become authenticated over there.
      window.location.href = json.data.url;
    } catch (err) {
      alert(err.message);
      setSwitching(false);
    }
  }

  const onMoreSection = moreItems.some(({ to }) => location.pathname.includes(`/${to}`));

  /* The manual "Sync OTAs" button lived here. Removed once properties moved
     onto the channel manager, which pushes and receives in near real time —
     a button that re-reads iCal calendars had nothing left to do on those
     properties, and invited staff to press it when a booking looked late.
     The 15-minute iCal scheduler (services/icalSyncService.js) is untouched
     and still runs for any tenant with feeds configured; POST /api/sync also
     remains, so this is a UI removal, not a capability one. */

  return (
    <div className="relative flex h-screen bg-white font-sans overflow-hidden">
      {/* Ambient colour blobs — flat gray behind a glass panel makes the
          blur invisible, so the frosted-glass surfaces below need actual
          colour/shape behind them to visibly pick up. Fixed + pointer-events
          none so they never affect layout or intercept clicks. z-0 (not a
          negative z-index) because a negative index here would paint BEHIND
          this root div's own background instead of on top of it — the root
          needs to be transparent/plain white (above) for the blobs to show
          through at all. */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -top-24 -left-20 w-96 h-96 rounded-full bg-amber-300/30 blur-3xl" />
        <div className="absolute top-1/3 -right-24 w-[28rem] h-[28rem] rounded-full bg-navy-300/25 blur-3xl" />
        <div className="absolute -bottom-32 left-1/4 w-96 h-96 rounded-full bg-orange-200/30 blur-3xl" />
      </div>

      {/* ── Sidebar (desktop only — mobile uses the bottom tab bar) ── */}
      <aside className={`hidden md:flex md:static
        ${sidebarOpen ? "md:w-64" : "md:w-16"}
        flex-shrink-0 bg-white/60 backdrop-blur-xl border-r border-white/40
        flex-col transition-all duration-300 ease-in-out shadow-lg shadow-black/5`}>

        {/* Logo — clickable → dashboard */}
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="flex items-center gap-3 px-4 py-5 border-b border-gray-100
            hover:bg-amber-50 transition-colors w-full text-left"
        >
          {logoUrl ? (
            <img src={logoUrl} alt={hotelName} className="w-8 h-8 rounded-lg object-cover flex-shrink-0 shadow-sm" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500
              flex items-center justify-center flex-shrink-0 shadow-sm">
              <Home size={16} className="text-white" />
            </div>
          )}
          {sidebarOpen && (
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">{hotelName.split(" ")[0]}</p>
              <p className="text-[10px] text-gray-400 tracking-widest uppercase">PMS</p>
            </div>
          )}
        </button>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {nav.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 min-h-[44px] rounded-xl text-sm font-medium
                transition-all duration-150 group relative
                ${isActive
                  ? "bg-amber-50 text-amber-700"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                }`}>
              {({ isActive }) => (
                <>
                  <Icon size={18} className={isActive ? "text-amber-600" : "text-gray-400 group-hover:text-gray-600"} />
                  {sidebarOpen && <span>{label}</span>}
                  {to === "bookings" && unreadCount > 0 && (
                    <span className="ml-auto bg-rose-500 text-white text-[10px] font-bold
                      rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom controls */}
        <div className="p-3 border-t border-gray-100 space-y-1">
          <button onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center justify-center gap-2 px-3 min-h-[44px]
              text-xs font-medium text-gray-600
              hover:text-gray-900
              hover:bg-gray-50 rounded-xl transition-all">
            <KeyRound size={14} />
            {sidebarOpen && "Change Password"}
          </button>
          <button onClick={() => setShowLogout(true)}
            className="w-full flex items-center justify-center gap-2 px-3 min-h-[44px]
              text-xs font-medium text-red-400 hover:text-red-600
              hover:bg-red-50 rounded-xl transition-all">
            <LogOut size={14} />
            {sidebarOpen && "Sign Out"}
          </button>
        </div>

        <div className="px-3 pb-2 border-t border-gray-100 pt-1">
          <PoweredByHostSync compact={!sidebarOpen} />
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="relative z-30 bg-white/60 backdrop-blur-xl border-b border-white/40 safe-top
          px-3 sm:px-6 py-4 flex items-center justify-between gap-2">
          <button onClick={() => setSidebarOpen(v => !v)}
            aria-label="Toggle sidebar"
            className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0">
            <Menu size={18} />
          </button>
          <span className="md:hidden font-bold text-gray-900 text-sm">
            {NAV.find(n => location.pathname.includes(`/${n.to}`))?.label || hotelName.split(" ")[0]}
          </span>

          {/* Property switcher — only for accounts linked to another property.
              Icon-only on phones so it never competes with the page title. */}
          {properties.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowPropMenu(v => !v)}
                aria-label="Switch property"
                className="tap-44 flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-xl
                  hover:bg-gray-100 transition-colors max-w-[40vw] sm:max-w-xs">
                <Building2 size={15} className="text-gray-400 flex-shrink-0" />
                <span className="hidden sm:inline text-sm font-semibold text-gray-900 truncate">{hotelName}</span>
                <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
              </button>
              {showPropMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowPropMenu(false)} />
                  <div className="absolute left-0 top-12 z-40 w-64 max-w-[calc(100vw-2rem)]
                    bg-white/80 backdrop-blur-2xl rounded-2xl shadow-2xl shadow-black/10
                    border border-white/50 p-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">
                      Switch property
                    </p>
                    <p className="px-2 pb-1.5 text-sm font-semibold text-amber-600 truncate">{hotelName}</p>
                    {properties.map(p => (
                      <button key={p.slug} disabled={switching}
                        onClick={() => switchProperty(p.slug)}
                        className="w-full text-left px-2.5 py-2.5 min-h-[44px] rounded-xl text-sm
                          text-gray-700 hover:bg-amber-50 disabled:opacity-50 transition-colors truncate">
                        {p.name}
                      </button>
                    ))}
                    {switching && <p className="px-2.5 py-1 text-xs text-gray-400">Switching…</p>}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap justify-end">
            {/* Notification bell */}
            <div className="relative">
              <button
                onClick={() => { setShowBellMenu(v => !v); if (unreadCount > 0) markAllRead(); }}
                aria-label="Notifications"
                className="relative flex items-center justify-center w-11 h-11 hover:bg-gray-100 rounded-xl transition-colors">
                <Bell size={18} className="text-gray-500" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4
                    bg-rose-500 text-white text-[9px] font-bold rounded-full
                    flex items-center justify-center animate-bounce">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {showBellMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowBellMenu(false)} />
                  <div className="absolute right-0 top-10 z-40 w-80 max-w-[calc(100vw-2rem)] bg-white/70 backdrop-blur-2xl
                    rounded-2xl shadow-2xl shadow-black/10 border border-white/50 p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mb-2">Notifications</p>
                    {feed.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-6">No notifications yet</p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-1">
                        {feed.map(n => {
                          const { Icon, color } = NOTIFICATION_ICONS[n.type] || { Icon: Bell, color: "text-gray-600 bg-gray-100" };
                          return (
                            <div key={n._id} className="flex items-start gap-2.5 p-2 rounded-xl hover:bg-gray-50 transition-colors">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                                <Icon size={13} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 leading-tight">{n.title}</p>
                                {n.message && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{n.message}</p>}
                                <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <a href="/book" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-medium text-white
                bg-gradient-to-r from-amber-500 to-orange-500
                hover:from-amber-600 hover:to-orange-600
                px-3 sm:px-4 min-h-[44px] rounded-full shadow-sm transition-all whitespace-nowrap">
              + New Booking
            </a>

            {/* Who is signed in. Name and role from the laptop breakpoint up;
                a phone header has room for the photo only, which is still a
                44px target straight to the profile. */}
            <NavLink to="profile" title={me?.fullName ? `${me.fullName} — my profile` : "My profile"}
              className={({ isActive }) => `flex items-center gap-2.5 min-h-[44px] min-w-[44px] pl-1 lg:pr-3 rounded-full
                transition-colors ${isActive ? "bg-amber-50" : "hover:bg-gray-100"}`}>
              <Avatar name={me?.fullName} src={me?.avatarUrl} size={36} />
              <span className="hidden lg:block text-left leading-tight max-w-[150px]">
                <span className="block text-xs font-semibold text-gray-900 truncate">{me?.fullName || "My profile"}</span>
                <span className="block text-[11px] text-gray-400 truncate">{ROLE_LABEL[role] || role}</span>
              </span>
            </NavLink>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden pb-16 md:pb-0">
          <Outlet />
        </main>
      </div>

      {/* ── Bottom tab bar (mobile only) — native-app nav instead of a hamburger drawer ── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/70 backdrop-blur-xl border-t border-white/40
        safe-bottom flex items-stretch">
        {tabs.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 relative
              ${isActive ? "text-amber-600" : "text-gray-400"}`}>
            {({ isActive }) => (
              <>
                <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                <span className="text-[10px] font-medium">{label}</span>
                {to === "bookings" && unreadCount > 0 && (
                  <span className="absolute top-1 right-1/4 bg-rose-500 text-white text-[9px] font-bold
                    rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
        <button onClick={() => setMoreSheetOpen(true)}
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2
            ${onMoreSection ? "text-amber-600" : "text-gray-400"}`}>
          <MoreHorizontal size={20} strokeWidth={onMoreSection ? 2.5 : 2} />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </nav>

      {/* ── "More" sheet (mobile only) ── */}
      {moreSheetOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setMoreSheetOpen(false)} />
          <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/75 backdrop-blur-2xl rounded-t-3xl shadow-2xl
            border-t border-white/50 safe-bottom max-h-[75vh] overflow-y-auto">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mt-3" />
            <div className="p-4 grid grid-cols-3 gap-3">
              {moreItems.map(({ to, label, Icon }) => (
                <NavLink key={to} to={to} onClick={() => setMoreSheetOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-1.5 py-4 rounded-2xl
                    ${isActive ? "bg-amber-50 text-amber-700" : "bg-gray-50 text-gray-600"}`}>
                  <Icon size={20} />
                  <span className="text-xs font-medium">{label}</span>
                </NavLink>
              ))}
            </div>
            <div className="p-4 pt-0 space-y-2">
              <button onClick={() => { setMoreSheetOpen(false); setShowChangePassword(true); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-3
                  text-sm font-medium text-gray-600 bg-gray-50 rounded-2xl">
                <KeyRound size={16} /> Change Password
              </button>
              <button onClick={() => { setMoreSheetOpen(false); setShowLogout(true); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-3
                  text-sm font-medium text-red-500 bg-red-50 rounded-2xl">
                <LogOut size={16} /> Sign Out
              </button>
              <PoweredByHostSync />
            </div>
          </div>
        </>
      )}

      {/* Logout modal */}
      {showLogout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 p-6 w-80 max-w-full text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogOut size={22} className="text-red-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Sign Out?</h3>
            <p className="text-sm text-gray-500 mb-5">You'll need to sign in again.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowLogout(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={logout}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600">
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          authHeaders={authHeaders}
        />
      )}

      <BookingToastContainer />
    </div>
  );
}
